import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface NavbarProps {
  activeLink?: 'home' | 'dashboard' | 'features';
}

export function Navbar({ activeLink }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Determine active link from props or location
  const getActiveLink = () => {
    if (activeLink) return activeLink;
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/dashboard') return 'dashboard';
    if (location.pathname === '/features') return 'features';
    return '';
  };

  const currentActive = getActiveLink();

  const navLinks = [
    { id: 'dashboard', label: 'Business Models', href: '/dashboard' },
    { id: 'features', label: 'Features', href: '/features' },
    { id: 'contact', label: 'Contact', href: '#', external: false },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="FinModel Logo"
              className="h-8 w-auto"
              style={{ height: '32px', width: 'auto' }}
              onError={(e) => {
                // Fallback if logo doesn't load
                e.currentTarget.style.display = 'none';
              }}
            />
            <span className="text-xl font-bold text-slate-900 tracking-tight">
              FinModel
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.id}
                  href={link.href}
                  className="text-slate-600 hover:text-primary-600 font-medium transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.id}
                  to={link.href}
                  className={`font-medium transition-colors ${currentActive === link.id
                      ? 'text-primary-600'
                      : 'text-slate-600 hover:text-primary-600'
                    }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="md:hidden bg-white border-b border-slate-200"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.id}
                    href={link.href}
                    className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.id}
                    to={link.href}
                    className={`block px-4 py-2 rounded-lg font-medium ${currentActive === link.id
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
